import { useCallback, useEffect, useMemo, useRef } from 'react';
import { updateData } from '../utils/firestoreService';

// Different fields may save concurrently. Writes to one field are ordered so an
// older request cannot overwrite a newer value in storage or in the editor.
export function useDocumentFieldSaves({ patientId, docId, documentData, setDocumentData,
  canEdit, onStatus, onCommitted }) {
  const session = useMemo(() => ({ patientId, docId, queues: new Map(), versions: new Map(), confirmed: new Map(),
    pending: 0, errors: new Set(), active: true }), [patientId, docId]);
  const current = useRef();
  current.current = { session, documentData, canEdit, onStatus, onCommitted };
  useEffect(() => {
    session.active = true;
    return () => { session.active = false; };
  }, [session]);
  useEffect(() => {
    // Adopt fresh document reads, while retaining confirmed results between
    // chained saves that finish before React has rendered their state updates.
    for (const field of session.confirmed.keys()) {
      if (!session.queues.has(field)) session.confirmed.set(field, documentData?.[field]);
    }
  }, [session, documentData]);

  const saveField = useCallback((field, getPatch, { onSuccess, onError } = {}) => {
    const isCurrent = () => session.active && current.current.session === session;
    if (!isCurrent() || !current.current.canEdit) return Promise.resolve(false);
    if (!session.confirmed.has(field)) {
      session.confirmed.set(field, current.current.documentData?.[field]);
    }
    const version = (session.versions.get(field) || 0) + 1;
    session.versions.set(field, version);
    session.errors.delete(field);
    session.pending += 1;
    current.current.onStatus('saving');
    const isLatest = () => isCurrent() && session.versions.get(field) === version;

    const task = (session.queues.get(field) || Promise.resolve()).then(async () => {
      try {
        if (!isCurrent()) return false;
        if (!current.current.canEdit) throw new Error('Editing is no longer available.');
        const patch = getPatch();
        await updateData(`patients/${session.patientId}/documents`, session.docId, patch);
        session.confirmed.set(field, patch[field]);
        if (!isCurrent()) return true;
        current.current.onCommitted?.();
        if (isLatest()) {
          setDocumentData(previous => ({ ...previous, [field]: patch[field] }));
          onSuccess?.();
        }
        return true;
      } catch (error) {
        if (isLatest()) {
          session.errors.add(field);
          const stored = session.confirmed.get(field);
          setDocumentData(previous => ({ ...previous, [field]: stored }));
          onError?.(stored);
          console.error(`Error saving ${field}:`, error);
        }
        return false;
      } finally {
        session.pending -= 1;
        if (isLatest()) session.queues.delete(field);
        if (isCurrent()) {
          current.current.onStatus(session.pending ? 'saving' : session.errors.size ? 'error' : 'saved');
        }
      }
    });
    session.queues.set(field, task);
    return task;
  }, [session, setDocumentData]);

  const waitForSaves = useCallback(async () => {
    while (session.pending) await Promise.all([...session.queues.values()]);
    return session.active && current.current.session === session && !session.errors.size;
  }, [session]);

  return { saveField, waitForSaves };
}
